import ClearIcon from '@mui/icons-material/Clear';
import { Box, IconButton, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { formatDate, formatDateTimeWithSeconds } from '../../app/utils';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid'; // To generate a new GUID
import { useAppSelector } from '../../app/hooks';
import { useGetTemperatureDataMutation } from '../../app/redux-fetch/apiQuery';
import { useGetShipmentByIdQuery } from '../../app/redux-fetch/apiOrders';
import ConditionalRender from '../../components/ConditionalRender/ConditionalRender';
import SpinnerBoxBlock from '../../components/Spinner/SpinnerBoxBlock';
import { fetchLoggedInUserSelector } from '../selectors';
import { ILoggedInUser } from '../types';
import ColoredLoadDropdown from './ColoredLoadDropdown';
import LoadTemperatureCard from './LoadTemperatureCard';
import LoadTemperatureReadTable from './LoadTemperatureReadTable';
import { TemperatureTabTile } from './TemperatureTabTile';
import { buildTemperatureWindow } from './temperatureWindow';
import { LoadIdAggregatedTemperatureData, PreparedChartData, TemperatureChartProps, TemperatureSeries } from './types';



function aggregateByLoadId(data: TemperatureSeries[]): LoadIdAggregatedTemperatureData {
    return data.reduce((acc, curr) => {
        if (!acc[curr.loadId]) {
                        acc[curr.loadId] = {
                            externalInsertionDate: formatDate(curr.externalInsertionDate),
                sensor1: curr.sensor1,
                setPointSum: curr.setPoint,
                returnAirSum: curr.returnAir,
                dischargeAirSum: curr.dischargeAir
            };
        } else {
            acc[curr.loadId].setPointSum += curr.setPoint;
            acc[curr.loadId].returnAirSum += curr.returnAir;
            acc[curr.loadId].dischargeAirSum += curr.dischargeAir;
        }
        return acc;
    }, {} as LoadIdAggregatedTemperatureData);
}

function getAverageTemperature(data: TemperatureSeries[], loadId: number) {
    let count = 0
    const totalTemp = data.reduce((acc: number, curr) => {
        if (curr.loadId === loadId) {
            count++
            acc += curr.sensor1

        }
        return acc
    }, 0)

    return parseFloat((totalTemp / count).toFixed(4));
}

function findLatestSensor1ValueByLoadId(data: TemperatureSeries[], loadId: number): number | undefined {
    const filteredData = data.filter(item => item.loadId === loadId);
    filteredData.sort((a, b) => new Date(b.externalInsertionDate).getTime() - new Date(a.externalInsertionDate).getTime());
    return filteredData.length > 0 ? filteredData[0].sensor1 : 0;
}

function findLatestReadDateByLoadId(data: TemperatureSeries[], loadId: number): string | Date {
    const filteredData = data.filter(item => item.loadId === loadId);
    filteredData.sort((a, b) => new Date(b.externalInsertionDate).getTime() - new Date(a.externalInsertionDate).getTime());
    return filteredData.length > 0 ? formatDateTimeWithSeconds(filteredData[0].externalInsertionDate) : formatDateTimeWithSeconds(new Date());
}

function findMaxSensor1ValueByLoadId(data: TemperatureSeries[], loadId: number): number | undefined {
    const sensor1Values = data.filter(item => item.loadId === loadId).map(item => item.sensor1);

    if (sensor1Values.length === 0) return undefined;
    return Math.max(...sensor1Values);
}

function findMinSensor1ValueByLoadId(data: TemperatureSeries[], loadId: number): number | undefined {
    const sensor1Values = data.filter(item => item.loadId === loadId).map(item => item.sensor1);

    if (sensor1Values.length === 0) return undefined;
    return Math.min(...sensor1Values);
}


function escapeHtml(str: string): string {
    return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
}

function getLoadDisplayName(load: any): string {
    if (load.description && load.description.trim() !== '') {
        return load.description;
    }

    const contentsDescriptions = (load.contents ?? [])
        .map((c: any) => c.description)
        .filter((d: string | undefined): d is string => !!d && d.trim() !== '');

    if (contentsDescriptions.length > 0) {
        return contentsDescriptions.join(' / ');
    }

    return `Load #${load.id}`;
}

function aggregateByDate(data: TemperatureSeries[], loads: any[]): PreparedChartData {
    const numLoads = loads.length;
    const loadIndexMap = new Map<number, number>();
    loads.forEach((load: any, idx: number) => {
        loadIndexMap.set(load.id, idx);
    });

    const result: PreparedChartData = {
        data: [],
        seriesNames: [],
        upperThreshold: {},
        lowerThreshold: {},
        setPoint: {},
        upperCritical: {},
        lowerCritical: {},
    };

    const dateMap = new Map<number, any[]>();

    data.forEach((curr) => {
        const load = loads.find((l: any) => l.id === curr.loadId);
        if (!load) return;

        const loadName = getLoadDisplayName(load);
        const colIdx = loadIndexMap.get(curr.loadId);
        if (colIdx === undefined) return;

        if (!(loadName in result.upperThreshold)) {
            result.upperThreshold[loadName] = load.temperatureSetting.upperThresholdWarning;
        }
        if (!(loadName in result.lowerThreshold)) {
            result.lowerThreshold[loadName] = load.temperatureSetting.lowerThresholdWarning;
        }
        if (!(loadName in result.setPoint)) {
            result.setPoint[loadName] = load.temperatureSetting.setPoint;
        }
        if (!(loadName in result.upperCritical)) {
            result.upperCritical[loadName] = load.temperatureSetting.upperThresholdCritical;
        }
        if (!(loadName in result.lowerCritical)) {
            result.lowerCritical[loadName] = load.temperatureSetting.lowerThresholdCritical;
        }

        const timestamp = new Date(curr.externalInsertionDate).getTime();

        if (!dateMap.has(timestamp)) {
            const row: any[] = [timestamp];
            for (let i = 0; i < numLoads; i++) row.push(null);
            dateMap.set(timestamp, row);
        }

        const row = dateMap.get(timestamp)!;
        row[colIdx + 1] = curr.sensor1.toString();
    });

    result.data = Array.from(dateMap.values()).sort((a, b) =>
        a[0] - b[0]
    );

    return result;
}


function solidChart(chartData: any) {

    const chart = anychart.line()

    const seriesColors: any[] = []
    const seriesList: any[] = []
    const markerIndices: number[][] = []

    // set chart padding
    chart.padding([10, 20, 5, 20]);

    // turn on chart animation
    chart.animation(true);

    // turn on the crosshair
    chart.crosshair(true);

    // set chart title text settings
    chart.title('Customers Activity during the Week');

    // set y axis title
    chart.yAxis().title('Temperature (°C)');

    // Use linear scale that auto-fits to the data range with padding
    const linearScale = anychart.scales.linear();
    linearScale.softMinimum(0).softMaximum(10);
    linearScale.minimumGap(0.1).maximumGap(0.1);
    chart.yScale(linearScale);

    // Use dateTime x-scale with daily tick labels (data stays at full granularity)
    const dateTimeScale = anychart.scales.dateTime();
    chart.xScale(dateTimeScale);
    chart.xScale().ticks().interval('day', 1);
    chart.xAxis().labels().format(function(this: any) {
        const d = new Date(this.value);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return year + '-' + month + '-' + day;
    });

    // Configure HTML tooltip styled as a card
    chart.tooltip().useHtml(true);
    chart.tooltip().displayMode('single');
    chart.tooltip().padding(0);
    chart.tooltip().background(false);
    chart.tooltip().allowLeaveChart(true);
    chart.tooltip().title(false);
    chart.tooltip().separator(false);

    // Inject CSS to remove AnyChart's default tooltip container styling (grey ring)
    if (!document.getElementById('anychart-tooltip-override')) {
        const style = document.createElement('style');
        style.id = 'anychart-tooltip-override';
        style.textContent = '.anychart-tooltip { background: none !important; border: none !important; box-shadow: none !important; padding: 0 !important; border-radius: 0 !important; }';
        document.head.appendChild(style);
    }

    // create data set on our data,also we can pud data directly to series
    const dataSet = anychart.data.set(chartData.data);


    let markerIndex = 1

    chartData.seriesNames.map((seriesName: any, idx: number) => {
        const series = chart.line(dataSet.mapAs({ x: 0, value: idx + 1 }));
        series.name(seriesName);
        series.markers().enabled(true).type('circle');
        series.legendItem().iconType('circle');

        seriesColors.push(series.color()); // Store series color
        seriesList.push(series);

        // Configure per-series tooltip styled as a card matching LoadTemperatureCard
        const color = series.color();
        const upper = chartData.upperThreshold[seriesName];
        const lower = chartData.lowerThreshold[seriesName];
        const setPointVal = chartData.setPoint[seriesName];
        const upperCriticalVal = chartData.upperCritical[seriesName];
        const lowerCriticalVal = chartData.lowerCritical[seriesName];
        series.tooltip().format(function(this: any) {
            const tempValue = parseFloat(this.value);
            const isOutOfRange = tempValue > upper || tempValue < lower;
            const tempColor = isOutOfRange ? '#D32F2F' : '#04C000';
            return '<div style="' +
                'font-family: Roboto, sans-serif;' +
                'background: #fff;' +
                'border: 1px solid #0086D4;' +
                'border-radius: 8px;' +
                'min-width: 340px;' +
                'max-width: 420px;' +
                'box-shadow: 2px 2px 6px rgba(0,0,0,0.15);' +
                'overflow: hidden;' +
                '">' +
                '<div style="display: flex; align-items: center; padding: 10px 14px 6px 14px;">' +
                    '<span style="display: inline-block; width: 14px; height: 14px; border-radius: 50%; background: ' + color + '; margin-right: 8px; flex-shrink: 0;"></span>' +
                    '<span style="font-size: 16px; font-weight: 500; color: #00a0e4; flex: 1;">' + escapeHtml(seriesName) + '</span>' +
                    '<span style="font-size: 12px; color: #666;">' + escapeHtml(dayjs(this.x).format('YYYY-MM-DD HH:mm')) + '</span>' +
                '</div>' +
                '<div style="border-bottom: 1px solid #00a0e4; margin: 0 14px;"></div>' +
                '<div style="padding: 8px 14px 4px 14px; font-size: 13px; font-weight: 700; color: #666;">Temperature Details</div>' +
                '<div style="padding: 4px 14px 10px 14px;">' +
                    '<table style="width: 100%; border-collapse: collapse; font-size: 12px; color: #000;">' +
                        '<tr>' +
                            '<td style="padding: 3px 8px 3px 0;">Temperature <b style="color: ' + tempColor + ';">' + escapeHtml(String(tempValue)) + '°C</b></td>' +
                            '<td style="padding: 3px 8px 3px 0;">Set Point <b>' + escapeHtml(String(setPointVal)) + '°C</b></td>' +
                        '</tr>' +
                        '<tr>' +
                            '<td style="padding: 3px 8px 3px 0;">Upper Warning <b>' + escapeHtml(String(upper)) + '°C</b></td>' +
                            '<td style="padding: 3px 8px 3px 0;">Upper Critical <b>' + escapeHtml(String(upperCriticalVal)) + '°C</b></td>' +
                        '</tr>' +
                        '<tr>' +
                            '<td style="padding: 3px 8px 3px 0;">Lower Warning <b>' + escapeHtml(String(lower)) + '°C</b></td>' +
                            '<td style="padding: 3px 8px 3px 0;">Lower Critical <b>' + escapeHtml(String(lowerCriticalVal)) + '°C</b></td>' +
                        '</tr>' +
                    '</table>' +
                '</div>' +
            '</div>';
        });

        if (chartData.upperCritical[seriesName] !== undefined) {
            const upperMarkerIdx = markerIndex;
            const lowerMarkerIdx = markerIndex + 1;
            markerIndices.push([upperMarkerIdx, lowerMarkerIdx]);

            chart.lineMarker(upperMarkerIdx).value(chartData.upperCritical[seriesName]).axis(chart.yAxis(0)).stroke({
                thickness: 2,
                color: seriesColors[idx],
                dash: '5 5',
            })
            chart.lineMarker(lowerMarkerIdx).value(chartData.lowerCritical[seriesName]).axis(chart.yAxis(0)).stroke({
                thickness: 2,
                color: seriesColors[idx],
                dash: '5 5',
            })
            markerIndex += 2
        } else {
            markerIndices.push([]);
        }
    })



    chart.legend().enabled(true).fontSize(13).padding([0, 0, 20, 0]).align('left')

    return { chart, seriesColors, seriesList, markerIndices }
}


const TemperatureChart = ({ data, title }: TemperatureChartProps) => {
    const chartContainerId = useRef(`chart-container-${uuidv4()}`)
    const chartRef = useRef<any>(null)
    const seriesListRef = useRef<any[]>([])
    const markerIndicesRef = useRef<number[][]>([])
    const { id } = useParams()
    const shipmentId = Number(id)
    const loggedInUser = useAppSelector(fetchLoggedInUserSelector.data) || ({} as ILoggedInUser)
    const tenantId = loggedInUser?.tenantId

    const {
        data: shipment,
        isFetching: shipmentSelectorFetching,
        isSuccess: shipmentLoaded,
    } = useGetShipmentByIdQuery({
        shipmentId,
        tenantId,
    })

    const loads = useMemo(() =>
        shipment?.bookings?.flatMap((booking: any) => booking.loads ?? []) ?? [],
        [shipment]
    )

    const [temperatureSeries, setTemperatureSeries] = useState<TemperatureSeries[]>([])
    const [chartData, setChartData] = useState<any>()

    useEffect(() => {

        if (window.anychart && chartData && shipment) {
            if (!chartRef.current) {
                const { chart, seriesColors, seriesList, markerIndices } = solidChart(chartData);
                seriesListRef.current = seriesList;
                markerIndicesRef.current = markerIndices;
                const loadColorData = loads?.map((load: any, idx: number) => {
                    return {
                        loadName: getLoadDisplayName(load),
                        loadId: load.id,
                        color: seriesColors[idx]
                    }
                }
                )
                setLoadDropdownValues(loadColorData)

                chartRef.current = chart
                chartRef.current.container(chartContainerId.current)
            }

            chartRef.current.title(title)
            chartRef.current.draw()
        }
    }, [data, title, chartData, shipment])

    const [temperatureData, setTemperatureData] = useState<any>({
        color: '',
        loadName: 'Load 1 Temperature Data',
        date: '10 Oct 2023 09:00:25 AM',
        setPoint: '8',
        tempRange: '2 to 8',
        latestReading: '7.4',
        average: '7.6',
        upperThreshold: '11',
        upperCritical: '13',
        lowerThreshold: '5',
        lowerCritical: '3',
    })

    const [loadDropDownValues, setLoadDropdownValues] = useState<any[]>([])
    const [useGetTemperatureData, { data: tempData, error, isLoading, isSuccess }] = useGetTemperatureDataMutation()


    useEffect(() => {
        const fetchTemperatureData = async () => {
            const loadIds: number[] = (loads?.map((load: any) => load.id) as number[])
            const temperatureSeriesResponse = await useGetTemperatureData({
                tenantId,
                shipmentId,
                body: {
                    loadIds,
                    ...buildTemperatureWindow(shipment?.validFrom),
                }
            })
            if ('data' in temperatureSeriesResponse) {
                if (temperatureSeriesResponse.data.length > 0) {
                    setTemperatureSeries(temperatureSeriesResponse.data);
                }
            }
        }
        if (shipmentLoaded && shipment && temperatureSeries.length > 0) {
            const result = aggregateByLoadId(temperatureSeries)

            const preparedChartData = aggregateByDate(temperatureSeries, loads)

            loads?.map((load: any) => {
                preparedChartData.seriesNames.push(getLoadDisplayName(load))
            }
            )

            setChartData(preparedChartData)
        }
        else {
            fetchTemperatureData()
        }
    }, [shipmentLoaded, shipment, temperatureSeries])

    const [selectedLoad, setSelectedLoad] = useState<any>()

    useEffect(() => {
        if (!chartRef.current || seriesListRef.current.length === 0) return;

        const selectedIdx = loadDropDownValues.findIndex((l: any) => l.loadId === selectedLoad);

        seriesListRef.current.forEach((series: any, idx: number) => {
            const visible = !selectedLoad || idx === selectedIdx;
            series.enabled(visible);
            const markers = markerIndicesRef.current[idx];
            if (markers) {
                markers.forEach((mi: number) => {
                    chartRef.current.lineMarker(mi).enabled(visible);
                });
            }
        });
    }, [selectedLoad, loadDropDownValues]);

    const handleClear = () => {
        setSelectedLoad(null);
        setDropdownValue('');
    };
    const [dropdownValue, setDropdownValue] = useState('');
    const handleDropdownChange = (event: any) => {
        setDropdownValue(event.target.value);
        const { loadId, loadName } = loadDropDownValues.find((load: any) => load.color === event.target.value)
        const temperatureData = loads?.find((load: any) => load.id === loadId)?.temperatureSetting
        if (temperatureData) {
            setTemperatureData({
                ...temperatureData,
                color: event.target.value,
                date: findLatestReadDateByLoadId(temperatureSeries, loadId),
                loadName: loadName,
                latestReading: findLatestSensor1ValueByLoadId(temperatureSeries, loadId),
                average: getAverageTemperature(temperatureSeries, loadId).toString(),
                tempRange: `${findMinSensor1ValueByLoadId(temperatureSeries, loadId)} to ${findMaxSensor1ValueByLoadId(temperatureSeries, loadId)}`,
                lowerCritical: temperatureData.lowerThresholdCritical.toString(),
                lowerThreshold: temperatureData.lowerThresholdWarning.toString(),
                upperCritical: temperatureData.upperThresholdCritical.toString(),
                upperThreshold: temperatureData.upperThresholdWarning.toString(),
            })
        }
        setSelectedLoad(loadId)
    };


    if (isLoading) {
        return (<Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="50vh" // for the full viewport height, or use a specific value
            width="90vw" // for the full viewport width, or use a specific value
        >
            <SpinnerBoxBlock />
        </Box>
        )
    }
    if (isSuccess && (error || !tempData || tempData.length === 0)) {

        return (<Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="50vh"
            width="90vw"
        >
            <Typography variant="h5" color="error">
                No Temperature Info Found
            </Typography>
        </Box>)
    }

    return (
        <TemperatureTabTile>
            <ConditionalRender condition={temperatureSeries.length > 0}>
                <Box sx={{
                    display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', width: '100%', pt: '20px', pb: '20px'
                }}>
                    <Box sx={{
                        display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center', width: '100%', pt: '20px', pb: '20px'
                    }}>
                        <ColoredLoadDropdown
                            values={loadDropDownValues}
                            value={dropdownValue}
                            onChange={handleDropdownChange}
                        />
                        <IconButton onClick={handleClear} aria-label="clear">
                            <ClearIcon />
                        </IconButton>
                    </Box>
                    <ConditionalRender condition={selectedLoad}>
                        <LoadTemperatureCard data={temperatureData} />
                    </ConditionalRender>
                </Box>
                <Box>
                    <Box id={chartContainerId.current}
                        style={{
                            width: '95%',
                            height: '500px',
                            margin: '20px',
                            padding: '20px',
                            borderRadius: '8px',
                            background: '#fff'
                        }}></Box>
                </Box>
                <Box>
                    <ConditionalRender condition={selectedLoad}>
                        <LoadTemperatureReadTable data={temperatureSeries.filter(x => x.loadId === selectedLoad)} load={loads?.find((load: any) => load.id === selectedLoad)} />
                    </ConditionalRender>
                </Box>
            </ConditionalRender>
        </TemperatureTabTile>

    )
}

export default TemperatureChart
